import { PRODUCT_PLACEHOLDERS } from "./product-placeholders";
// ---------------------------------------------------------------------------
// BOATING SUPPLIES — mirrored from supplier catalog for our own site.
//
// The supplier "boat" search returns 1,000+ SKUs across canvas, vinyl,
// windows, zippers, snaps and tops. We mirror by family — each card
// represents a complete product line — and the detail page lists the common
// sizes, finishes and use cases. Customers add to their order with notes,
// and we confirm SKUs and pricing on the written quote.
//
// Each product has its own internal detail page at /shop/boating/$slug so
// customers stay on our site instead of being redirected.
// ---------------------------------------------------------------------------

export type BoatingProduct = {
  id: string;
  slug: string;
  name: string;
  /** Headline format shown on the listing card */
  size: string;
  /** Reference item # / family code */
  itemNumber: string;
  /** Brand / manufacturer */
  manufacturer: string;
  /** Short blurb shown on the listing card */
  blurb: string;
  /** Long description shown on the detail page (paragraphs) */
  description: string[];
  /** Bullet-point feature list */
  features: string[];
  /** Spec table key/value pairs */
  specs: { label: string; value: string }[];
  /** Product image URL */
  image: string;
  /** Available colors / finishes */
  colors: string[];
  /** Available sizes / configurations */
  sizes: string[];
  /** Optional product note */
  productNote?: string;
};

export const boatingProducts: BoatingProduct[] = [
  {
    id: "sunbrella-marine-canvas",
    slug: "sunbrella-marine-grade-canvas",
    name: "Sunbrella® Marine Grade Canvas",
    size: '60" wide · By the yard',
    itemNumber: "BM-SUN-MARINE",
    manufacturer: "Glen Raven / Sunbrella®",
    blurb:
      "The industry-standard solution-dyed acrylic canvas for bimini tops, boat covers, dodgers and enclosures. 10-year marine warranty.",
    description: [
      "Sunbrella® Marine Grade is the gold-standard fabric for marine canvas work. Solution-dyed acrylic — color goes all the way through the fiber, not just on the surface — so it won't fade, crack or rot in full-time sun and salt exposure.",
      "Used worldwide for bimini tops, full enclosures, mooring covers, sail covers, dodgers and helm covers. Carries a 10-year limited fabric warranty against loss of color and strength.",
      "60\" wide, sold by the linear yard. Available in 100+ marine colors — popular shades include Captain Navy, Forest Green, Marine Blue, Linen, Pacific Blue, Burgundy, Charcoal Grey and Toast.",
    ],
    features: [
      "Solution-dyed acrylic — won't fade in sun or salt",
      "10-year marine warranty (Glen Raven)",
      "60\" usable width",
      "100+ marine colors",
      "Mildew & UV resistant",
      "Breathable — won't trap moisture under the cover",
    ],
    specs: [
      { label: "Product Category", value: "Marine Canvas" },
      { label: "Material", value: "Solution-dyed Acrylic" },
      { label: "Width", value: '60"' },
      { label: "Weight", value: "~9.25 oz/sq yd" },
      { label: "Warranty", value: "10 years (marine grade)" },
      { label: "Manufacturer", value: "Glen Raven / Sunbrella®" },
    ],
    image: PRODUCT_PLACEHOLDERS.boating,
    colors: [
      "Captain Navy",
      "Marine Blue",
      "Pacific Blue",
      "Forest Green",
      "Burgundy",
      "Charcoal Grey",
      "Linen",
      "Toast",
      "Natural",
      "Black",
      "White",
    ],
    sizes: ["1 yd", "5 yds", "10 yds", "25 yds", "Full roll"],
    productNote: "Tell us color and yardage in your order notes — we'll confirm availability and ship.",
  },
  {
    id: "stamoid-top-vinyl",
    slug: "stamoid-top-marine-vinyl",
    name: "Stamoid® Top Marine Vinyl",
    size: '62" wide · By the yard',
    itemNumber: "BM-STAMOID",
    manufacturer: "Serge Ferrari / Stamoid®",
    blurb:
      "Premium PVC-coated polyester top fabric — the European standard for high-end powerboat and sailboat enclosures. Smooth, waterproof, longest-lasting.",
    description: [
      "Stamoid® Top is a premium PVC-coated polyester fabric used on high-end yachts, powerboats and sailboats. Completely waterproof, dimensionally stable, and exceptionally smooth — water and salt slide off, and the fabric won't sag or stretch over its lifetime.",
      "Preferred by builders like Boston Whaler, Sea Ray and Hinckley for OEM dodger and bimini work. Holds up to a decade of full-time exposure with minimal fade.",
      "62\" wide, sold by the linear yard. Available in marine whites, navy, charcoal and a small range of premium colors.",
    ],
    features: [
      "PVC-coated polyester — fully waterproof",
      "Smooth surface — sheds water & salt instantly",
      "Dimensionally stable — won't sag or stretch",
      "OEM-spec for premium powerboats",
      "62\" wide",
      "10+ year service life",
    ],
    specs: [
      { label: "Product Category", value: "Marine Top Fabric" },
      { label: "Material", value: "PVC-coated Polyester" },
      { label: "Width", value: '62"' },
      { label: "Weight", value: "~16 oz/sq yd" },
      { label: "Manufacturer", value: "Serge Ferrari / Stamoid®" },
    ],
    image: PRODUCT_PLACEHOLDERS.boating,
    colors: ["White", "Captain Navy", "Charcoal", "Beige", "Sand", "Black", "Pacific Blue"],
    sizes: ["1 yd", "5 yds", "10 yds", "25 yds", "Full roll"],
    productNote: "Premium fabric — tell us color and yardage; we'll confirm pricing on the quote.",
  },
  {
    id: "strataglass-clear-vinyl-windows",
    slug: "strataglass-clear-vinyl-windows",
    name: "Strataglass™ Polished Clear Vinyl",
    size: '54" / 60" wide · 30 / 40 / 60 gauge',
    itemNumber: "BM-STRATA",
    manufacturer: "Strataglass™",
    blurb:
      "Premium scratch-resistant clear vinyl windows for enclosures, dodgers and bimini tops. The standard for high-end boats.",
    description: [
      "Strataglass™ is a polished, scratch-resistant clear vinyl used for enclosure windows, dodger lights and bimini side curtains. Optical clarity is excellent — gives a true \"glass\" view from inside the cockpit — and the proprietary surface coating resists hazing and surface scratches better than standard clear vinyl.",
      "Available in 30, 40 and 60 gauge thicknesses. 60-gauge is the premium choice for full enclosures and offshore use; 30/40 gauge is common for side curtains and lighter applications.",
      "Sold by the linear yard. Always store flat or rolled (never folded), and clean only with approved Strataglass™-safe cleaners — no ammonia, no alcohol.",
    ],
    features: [
      "Polished, optically clear vinyl",
      "Proprietary scratch-resistant coating",
      '54" or 60" usable width',
      "30 / 40 / 60 gauge thicknesses",
      "Industry standard for premium enclosures",
    ],
    specs: [
      { label: "Product Category", value: "Clear Vinyl Window" },
      { label: "Material", value: "Polished PVC" },
      { label: "Widths", value: '54" or 60"' },
      { label: "Gauges", value: "30, 40, 60 mil" },
      { label: "Manufacturer", value: "Strataglass™" },
    ],
    image: PRODUCT_PLACEHOLDERS.boating,
    colors: ["Clear (polished)"],
    sizes: ['30 ga · 54"', '40 ga · 54"', '60 ga · 54"', '40 ga · 60"', '60 ga · 60"'],
    productNote: "Use only Strataglass™-approved cleaner. Never fold — store rolled or flat.",
  },
  {
    id: "phifertex-mesh-marine",
    slug: "phifertex-marine-mesh",
    name: "Phifertex® Marine & Outdoor Mesh",
    size: '54" / 60" wide · By the yard',
    itemNumber: "BM-PHIFERTEX",
    manufacturer: "Phifer®",
    blurb:
      "Vinyl-coated polyester mesh for boat seats, drop curtains, sun-shade panels and ventilated enclosures.",
    description: [
      "Phifertex® is a vinyl-coated polyester mesh used widely for marine seat slings, drop curtains, ventilated boat covers and shade panels. Lets air and water through while blocking UV and providing privacy and shade.",
      "PVB (Plus) and standard versions are available. Phifertex® Plus carries a 5-year warranty against UV and mildew. Suitable for re-slinging deck chairs, building breathable enclosures, and adding shade panels to bimini tops.",
      "54\" and 60\" widths, sold by the linear yard. Wide range of solid and patterned colors.",
    ],
    features: [
      "Vinyl-coated polyester mesh",
      "Blocks UV; lets air and water pass",
      "Standard, Plus and Cane Wicker styles",
      '54" / 60" widths',
      "Mildew & rot resistant",
    ],
    specs: [
      { label: "Product Category", value: "Marine Mesh" },
      { label: "Material", value: "PVC-coated Polyester" },
      { label: "Widths", value: '54" / 60"' },
      { label: "Warranty", value: "Up to 5 years (Plus)" },
      { label: "Manufacturer", value: "Phifer®" },
    ],
    image: PRODUCT_PLACEHOLDERS.boating,
    colors: ["Black", "Beige", "Sand", "Forest Green", "Grey", "Cocoa", "Cane Wicker patterns"],
    sizes: ['54" wide', '60" wide'],
    productNote: "Tell us style (Standard / Plus / Cane Wicker), color and yardage in your notes.",
  },
  {
    id: "ykk-vislon-marine-zipper",
    slug: "ykk-vislon-marine-zipper-system",
    name: "YKK® VISLON® #10 Marine Zipper System",
    size: "#10 chain · By the yard + sliders",
    itemNumber: "BM-VISLON-10",
    manufacturer: "YKK",
    blurb:
      "The standard heavy-duty marine zipper for dodgers, enclosures, bimini boots and convertible tops. Salt-water and UV proof.",
    description: [
      "YKK® VISLON® #10 is the workhorse zipper for marine canvas. Molded polyacetal teeth — completely immune to salt and corrosion, won't seize up like a metal zipper, and slide smoothly even after years of exposure.",
      "We sell the chain by the yard and the sliders separately, so you only buy the length you need. Slider styles include single non-locking, double non-locking, single auto-lock and double auto-lock. Pair with stop-and-box sets to terminate the zipper cleanly.",
      "Standard colors are black and white. The most-installed marine zipper system in the world.",
    ],
    features: [
      "#10 size — heavy-duty marine standard",
      "Molded polyacetal teeth — salt & UV proof",
      "Chain sold by the yard for any length",
      "Sliders sold separately (single / double, locking / non-locking)",
      "Black and white standard colors",
    ],
    specs: [
      { label: "Product Category", value: "Marine Zipper" },
      { label: "Chain Size", value: "#10 (~10 mm)" },
      { label: "Tooth Material", value: "Molded Polyacetal" },
      { label: "Manufacturer", value: "YKK" },
    ],
    image: PRODUCT_PLACEHOLDERS.boating,
    colors: ["Black", "White"],
    sizes: ["Chain — by the yard", "Single non-lock slider", "Double non-lock slider", "Auto-lock slider", "Stop & box set"],
    productNote: "Tell us total yards of chain and slider style/quantity — we'll quote a complete kit.",
  },
  {
    id: "dot-marine-snap-fasteners",
    slug: "dot-marine-snap-fasteners",
    name: "DOT® Pull-the-DOT® & Marine Snap Fasteners",
    size: "Stainless / Brass · Multiple finishes",
    itemNumber: "BM-DOT-MARINE",
    manufacturer: "DOT Fastener",
    blurb:
      "The standard 4-part snap for boat covers, bimini boots and convertible tops. Stainless option for full-time salt exposure.",
    description: [
      "DOT® Durable™ and Pull-the-DOT® snap fasteners are the most-used canvas snaps in the marine trade. Each set is four parts (cap, socket, stud, eyelet) installed with a snap-setting tool. Pull-the-DOT® has a directional release — only opens when pulled in the correct direction — making it ideal for boat covers that need to stay snapped under wind load.",
      "Solid brass with optional nickel or stainless plating. The 316 stainless cap option is the right choice for full-time saltwater exposure.",
      "Available individually or in 100-count tradesman boxes. Color the cap to match your canvas.",
    ],
    features: [
      "4-part snap (cap / socket / stud / eyelet)",
      "Pull-the-DOT® directional release available",
      "Stainless option for offshore / saltwater",
      "Long-shank studs for thick canvas / vinyl stacks",
      "Sold individually or in 100-count boxes",
    ],
    specs: [
      { label: "Product Category", value: "Marine Snap Fasteners" },
      { label: "Material", value: "Brass / Stainless Steel" },
      { label: "Manufacturer", value: "DOT Fastener" },
    ],
    image: PRODUCT_PLACEHOLDERS.boating,
    colors: ["Nickel-plated brass", "Solid brass", "Stainless steel", "Black oxide"],
    sizes: ['Standard cap (3/8")', 'Long-shank stud (5/8")', 'XL stud (3/4")', "100-count tradesman box"],
    productNote: "Tell us snap type (Durable vs Pull-the-DOT®), finish and quantity in your order notes.",
  },
  {
    id: "bimini-frame-fittings",
    slug: "bimini-frame-fittings-stainless",
    name: "Bimini Frame Fittings — 316 Stainless",
    size: '7/8" / 1" / 1-1/4" tube · Multiple fittings',
    itemNumber: "BM-BIMINI-FIT",
    manufacturer: "Marine Hardware",
    blurb:
      "Eye ends, jaw slides, deck hinges, eye straps and quick-release pins for building or repairing bimini and dodger frames.",
    description: [
      "The complete fittings family for bimini tops, dodger frames and pilothouse frames. 316 marine-grade stainless steel — won't rust or stain canvas, even with full-time saltwater exposure.",
      "Includes eye ends, jaw slides, deck hinges, eye straps, deck mounts, quick-release pins, swivel hinges and end caps. Sized to fit 7/8\", 1\" and 1-1/4\" stainless or aluminum tubing.",
      "Tell us your existing tube OD and what you're replacing or building — we'll spec a complete kit.",
    ],
    features: [
      "316 marine stainless — coastal-rated",
      'Fits 7/8" / 1" / 1-1/4" tube',
      "Eye ends, jaw slides, deck hinges, eye straps, swivel hinges",
      "Quick-release pins for stowing the frame",
      "Sold individually or in matched kits",
    ],
    specs: [
      { label: "Product Category", value: "Bimini / Dodger Hardware" },
      { label: "Material", value: "316 Stainless Steel" },
      { label: "Tube Sizes", value: '7/8", 1", 1-1/4"' },
      { label: "Application", value: "Bimini tops, dodgers, pilothouse frames" },
    ],
    image: PRODUCT_PLACEHOLDERS.boating,
    colors: ["316 Stainless Steel"],
    sizes: ["Eye end", "Jaw slide", "Deck hinge", "Eye strap", "Quick-release pin", "Swivel hinge", "End cap"],
    productNote: "Tell us tube OD and the parts you need — we'll quote a complete fitting kit.",
  },
  {
    id: "shock-cord-and-anchor",
    slug: "shock-cord-and-anchors",
    name: "Marine Shock Cord & Cover Anchors",
    size: '1/8" – 3/8" cord · By the foot + anchors',
    itemNumber: "BM-SHOCK-CORD",
    manufacturer: "Marine Hardware",
    blurb:
      "Bungee shock cord plus stainless tie-down anchors for boat covers, mooring covers and tarp tie-downs.",
    description: [
      "The everyday tie-down system for boat and pontoon covers, mooring covers and shop tarps. Polyester-jacketed rubber shock cord (bungee) hooks the cover skirt under the rub rail and keeps tension even as the canvas relaxes in heat.",
      "Pair with stainless tie-down loops, snap hooks, ball-style cord ends or J-hooks to build a complete tie-down system. Cord sold by the foot in 1/8\", 3/16\", 1/4\" and 3/8\" diameters.",
      "Common colors are black and white; reflective and high-vis options available for night-time tie-downs.",
    ],
    features: [
      "Polyester-jacketed rubber shock cord",
      'Diameters: 1/8", 3/16", 1/4", 3/8"',
      "Sold by the foot — buy any length",
      "Stainless tie-down anchors, hooks and ball ends",
      "UV-stable jacket",
    ],
    specs: [
      { label: "Product Category", value: "Cover Tie-Down System" },
      { label: "Material", value: "Polyester-jacketed rubber" },
      { label: "Diameters", value: '1/8", 3/16", 1/4", 3/8"' },
      { label: "Anchors", value: "316 Stainless Steel" },
    ],
    image: PRODUCT_PLACEHOLDERS.boating,
    colors: ["Black", "White", "Reflective"],
    sizes: ['1/8" cord', '3/16" cord', '1/4" cord', '3/8" cord', "Ball end", "S-hook", "J-hook", "Stainless anchor"],
    productNote: "Tell us cord diameter, total feet, and number of anchors/hooks needed.",
  },
];

export function getBoatingBySlug(slug: string): BoatingProduct | undefined {
  return boatingProducts.find((p) => p.slug === slug);
}
